import React, { useEffect, useRef, useState } from 'react';
import { Activity, ChevronDown, ChevronUp, Loader2, RefreshCw, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { adminCodeHealthAPI } from '../services/api';
import TestSuitePanel from './TestSuitePanel';
import CatalogSyncCard from './CatalogSyncCard';

const TONE = {
  green: { box: 'bg-white border-gray-100', icon: <CheckCircle2 className="text-emerald-600" size={20} />, label: 'Saine', chip: 'bg-emerald-100 text-emerald-800' },
  warn: { box: 'bg-amber-50 border-amber-200', icon: <AlertTriangle className="text-amber-600" size={20} />, label: 'À surveiller', chip: 'bg-amber-100 text-amber-800' },
  red: { box: 'bg-red-50 border-red-200', icon: <XCircle className="text-red-600" size={20} />, label: 'Dégradée', chip: 'bg-red-100 text-red-800' },
};

const Kpi = ({ label, value, ok, testId }) => (
  <div className="rounded-lg bg-white/70 border border-gray-100 px-3 py-2" data-testid={testId}>
    <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
    <div className={`text-lg font-semibold ${ok ? 'text-emerald-700' : 'text-red-700'}`}>{value}</div>
  </div>
);

// Dashboard card: weekly code-health indicator (import cycles, lint, smoke tests, biggest modules)
const CodeHealthCard = () => {
  const [data, setData] = useState(null);
  const [suites, setSuites] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const load = () => adminCodeHealthAPI.get().then((d) => {
    setData(d);
    if (d.running) timer.current = setTimeout(load, 5000); else setBusy(false);
  }).catch(() => setData({ error: true }));
  useEffect(() => { load(); adminCodeHealthAPI.suites().then((d) => setSuites(d.suites || [])).catch(() => {}); return () => clearTimeout(timer.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data || data.error) return null;
  const tests = data.tests || {};
  const testsOk = tests.ran && !tests.failures && !tests.errors;
  const tone = TONE[data.status] || TONE.warn;

  const run = async () => {
    setBusy(true);
    try {
      const r = await adminCodeHealthAPI.run(true);
      toast.success(r.started ? 'Analyse lancée (2 à 4 minutes avec les tests)' : 'Analyse déjà en cours');
      timer.current = setTimeout(load, 4000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Lancement impossible'); setBusy(false); }
  };

  return (
    <div className={`rounded-xl shadow-sm p-5 mb-6 border ${data.at ? tone.box : 'bg-white border-gray-100'}`} data-testid="code-health-card">
      <div className="flex items-start justify-between gap-3">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-left flex-1" data-testid="code-health-toggle">
          {data.at ? tone.icon : <Activity className="text-gray-500" size={20} />}
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">Santé du code
              {data.at && <span className={`text-xs px-2 py-0.5 rounded-full ${tone.chip}`} data-testid="code-health-status">{tone.label}</span>}
              {(data.running || busy) && <Loader2 size={14} className="animate-spin text-gray-500" />}
            </h2>
            <p className="text-xs text-gray-600" data-testid="code-health-at">
              {data.at ? `Dernière analyse : ${new Date(data.at).toLocaleString('fr-FR')} · relancée chaque lundi par le cron · e-mail envoyé si « Dégradée » (et au retour au vert)` : 'Aucune analyse pour l’instant — lancée chaque lundi matin par le cron, ou à la demande.'}
            </p>
          </div>
          {data.at && (open ? <ChevronUp size={18} className="text-gray-500" /> : <ChevronDown size={18} className="text-gray-500" />)}
        </button>
        <button type="button" onClick={run} disabled={busy || data.running} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50" data-testid="code-health-run">
          <RefreshCw size={13} className={busy || data.running ? 'animate-spin' : ''} /> Analyser
        </button>
      </div>
      {data.at && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <Kpi label="Cycles d'imports" value={data.cycles.length} ok={!data.cycles.length} testId="code-health-cycles" />
          <Kpi label="Problèmes lint" value={data.lint.problems} ok={!data.lint.problems} testId="code-health-lint" />
          <Kpi label="Tests de fumée" value={tests.ran ? `${tests.passed}/${tests.tests - tests.skipped}` : '—'} ok={testsOk} testId="code-health-tests" />
          <Kpi label={`Fichiers > ${data.max_lines} lignes`} value={data.files.filter((f) => f.over).length} ok={!data.files.some((f) => f.over)} testId="code-health-files" />
        </div>
      )}
      {suites.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="code-health-suites">
          <h3 className="text-xs uppercase tracking-wide text-gray-500">Suites de tests à la demande</h3>
          {suites.map((s) => <TestSuitePanel key={s.key} suite={s} />)}
        </div>
      )}
      <CatalogSyncCard />
      {open && data.at && (
        <div className="mt-4 grid md:grid-cols-2 gap-4 text-sm" data-testid="code-health-details">
          <div>
            <h3 className="font-medium text-gray-800 mb-1">Modules les plus volumineux</h3>
            <ul className="divide-y divide-gray-100">
              {data.files.map((f) => <li key={f.file} className="flex justify-between py-1"><code className="text-xs">{f.file}</code><span className={f.over ? 'text-red-700 font-medium' : 'text-gray-600'}>{f.lines} l.</span></li>)}
            </ul>
          </div>
          <div className="space-y-3">
            {data.cycles.length > 0 && <div><h3 className="font-medium text-gray-800 mb-1">Cycles</h3><ul className="text-xs text-red-700 space-y-0.5">{data.cycles.map((c) => <li key={c}>{c}</li>)}</ul></div>}
            {data.lint.sample?.length > 0 && <div><h3 className="font-medium text-gray-800 mb-1">Lint</h3><ul className="text-xs text-red-700 space-y-0.5">{data.lint.sample.map((c) => <li key={c}><code>{c}</code></li>)}</ul></div>}
            <div>
              <h3 className="font-medium text-gray-800 mb-1">Tests</h3>
              {tests.ran
                ? <p className="text-xs text-gray-600">{tests.passed} réussi(s) · {tests.failures + tests.errors} en échec · {tests.skipped} ignoré(s) · imports inutilisés : {data.lint.unused_imports}</p>
                : <p className="text-xs text-gray-600">Non exécutés{tests.error ? ` : ${tests.error}` : ''}</p>}
              {tests.failed?.length > 0 && <ul className="text-xs text-red-700 mt-1 space-y-0.5">{tests.failed.map((t) => <li key={t}><code>{t}</code></li>)}</ul>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CodeHealthCard;
